import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Controller, useForm } from 'react-hook-form';
import { nameValidation, requiredValidation, usPhoneNumberValidation } from 'validators';
import { Page, Button, TextField, PasswordField, Card, USPhoneNumberInput, TextButton } from 'seagull';
import ChangeEmailAddressModal from '~/components/ChangeEmailAddressModal/ChangeEmailAddressModal';
import { useOverlayContext, useStore, useStoreActions } from '~/contexts';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { Intent } from '~/infra/access-control';
import { handleRequest } from '~/infra/utils';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import styles from './Profile.module.scss';
const testPrefix = 'profile';
const title = 'Profile';
const nullIfEmpty = (s)=>s || null;
export default function Profile() {
    const overlayContext = useOverlayContext();
    const userInfo = useStore((state)=>state.userInfo);
    const { setUserInfo, logBI, api } = useStoreActions();
    const { control, formState, handleSubmit, watch, trigger, clearErrors, reset } = useForm({
        mode: 'onChange'
    });
    const userLoggedInViaLink = userInfo === null || userInfo === void 0 ? void 0 : userInfo.loggedInViaLink();
    const userHasPassword = userInfo === null || userInfo === void 0 ? void 0 : userInfo.hasPassword();
    const firstNameValidator = nameValidation('First name', {
        required: 'Please enter your first name'
    });
    const lastNameValidator = nameValidation('Last name', {
        required: 'Please enter your last name'
    });
    const currentPasswordValidator = requiredValidation(!!watch('newPassword') && 'Please enter your current password');
    const newPasswordValidator = {
        validate (password) {
            const hasPassword = !!password;
            hasPassword ? trigger('currentPassword') : clearErrors('currentPassword');
            const isValidPassword = hasPassword ? (password === null || password === void 0 ? void 0 : password.length) >= 10 : true;
            return isValidPassword || 'Use 10 or more characters';
        }
    };
    const confirmPasswordValidator = {
        validate (password) {
            return password === watch('newPassword') || 'Passwords must match';
        }
    };
    useSetDocumentTitle(title);
    const onEditUserEmail = ()=>{
        overlayContext.showModal({
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ChangeEmailAddressModal, {
                    onClose: dismissModal
                });
            }
        });
    };
    const onSubmit = async (form)=>{
        logBI(10410);
        const updatedUserInfo = await handleRequest(api.auth.editUserDetails({
            name: {
                first: form.firstName,
                last: form.lastName
            },
            password: nullIfEmpty(form.newPassword),
            currentPassword: form.newPassword ? nullIfEmpty(form.currentPassword) : null,
            phone: nullIfEmpty(form.phone),
            clearPhoneNumber: !form.phone
        }));
        if (updatedUserInfo.success) {
            setUserInfo((userInfo === null || userInfo === void 0 ? void 0 : userInfo.updateUserDetails(updatedUserInfo.data)) || null);
            reset({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
            overlayContext.showSuccessToast('Changes saved');
        } else {
            overlayContext.showErrorToast(updatedUserInfo.error.updateUserDetailsErrorText());
        }
    };
    return /*#__PURE__*/ _jsx(Page, {
        header: title,
        skin: "secondary",
        testId: "".concat(testPrefix, "-page"),
        actions: /*#__PURE__*/ _jsx(Button, {
            intent: Intent.UpdateProfile,
            type: "submit",
            form: testPrefix,
            loading: formState.isSubmitting,
            testId: "".concat(testPrefix, "-btn-save-changes"),
            children: "Save changes"
        }),
        children: /*#__PURE__*/ _jsx("form", {
            id: testPrefix,
            onSubmit: handleSubmit(onSubmit),
            children: /*#__PURE__*/ _jsxs("div", {
                className: styles.root,
                children: [
                    /*#__PURE__*/ _jsxs(Section, {
                        title: "Personal details",
                        children: [
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "firstName",
                                defaultValue: userInfo === null || userInfo === void 0 ? void 0 : userInfo.firstName(),
                                rules: firstNameValidator,
                                render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                        collapseHelperText: false,
                                        className: styles.field
                                    }, connectControllerToTextField(renderProps)), {
                                        label: "First name",
                                        testId: "".concat(testPrefix, "-first-name")
                                    }))
                            }),
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "lastName",
                                defaultValue: userInfo === null || userInfo === void 0 ? void 0 : userInfo.lastName(),
                                rules: lastNameValidator,
                                render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                        collapseHelperText: false,
                                        className: styles.field
                                    }, connectControllerToTextField(renderProps)), {
                                        label: "Last name",
                                        testId: "".concat(testPrefix, "-last-name")
                                    }))
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: styles.emailContainer,
                                children: [
                                    /*#__PURE__*/ _jsx(TextField, {
                                        collapseHelperText: false,
                                        className: styles.field,
                                        value: userInfo === null || userInfo === void 0 ? void 0 : userInfo.email(),
                                        label: "Email address",
                                        type: "email",
                                        testId: "".concat(testPrefix, "-email"),
                                        disabled: true
                                    }),
                                    /*#__PURE__*/ _jsx(TextButton, {
                                        onClick: onEditUserEmail,
                                        underline: "none",
                                        testId: "".concat(testPrefix, "-email-edit-button"),
                                        type: "button",
                                        children: "Edit"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "phone",
                                defaultValue: (userInfo === null || userInfo === void 0 ? void 0 : userInfo.phone()) || undefined,
                                rules: usPhoneNumberValidation(),
                                render: (renderProps)=>/*#__PURE__*/ _jsx(USPhoneNumberInput, _object_spread_props(_object_spread({
                                        collapseHelperText: false,
                                        className: styles.field
                                    }, connectControllerToTextField(renderProps)), {
                                        label: "Phone number",
                                        testId: "".concat(testPrefix, "-phone")
                                    }))
                            })
                        ]
                    }),
                    (userHasPassword || userLoggedInViaLink) && /*#__PURE__*/ _jsxs(Section, {
                        title: "Password",
                        children: [
                            !userLoggedInViaLink && /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "currentPassword",
                                rules: currentPasswordValidator,
                                render: (renderProps)=>/*#__PURE__*/ _jsx(PasswordField, _object_spread_props(_object_spread({
                                        className: styles.field
                                    }, connectControllerToTextField(renderProps)), {
                                        label: "Current password",
                                        testId: "".concat(testPrefix, "-current-password")
                                    }))
                            }),
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "newPassword",
                                rules: newPasswordValidator,
                                render: (renderProps)=>/*#__PURE__*/ _jsx(PasswordField, _object_spread_props(_object_spread({
                                        className: styles.field
                                    }, connectControllerToTextField(renderProps)), {
                                        label: "New password",
                                        testId: "".concat(testPrefix, "-new-password")
                                    }))
                            }),
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "confirmPassword",
                                rules: confirmPasswordValidator,
                                render: (renderProps)=>/*#__PURE__*/ _jsx(PasswordField, _object_spread_props(_object_spread({
                                        className: styles.field
                                    }, connectControllerToTextField(renderProps)), {
                                        label: "Confirm password",
                                        testId: "".concat(testPrefix, "-confirm-password")
                                    }))
                            })
                        ]
                    })
                ]
            })
        })
    });
}
const Section = (props)=>{
    return /*#__PURE__*/ _jsx(Card, {
        padding: "m",
        autoHeight: true,
        title: title,
        children: /*#__PURE__*/ _jsx("div", {
            className: styles.content,
            children: props.children
        })
    });
};
